import { optimizeImage, optimizeUnsplash, optimizeWikimedia } from '../../utils/imageOptimizer';

/**
 * Helper function to optimize background images for styled-components
 *
 * Usage in styled-components:
 * import { optimizeBackgroundImage } from '../../components/common/OptimizedBackgroundImage';
 *
 * const HeroSection = styled.div`
 *   background-image: url(${props => optimizeBackgroundImage(props.imageUrl, { width: 1920, height: 1080 })});
 *   background-size: cover;
 *   background-position: center;
 * `;
 *
 * Or use getOptimizedBackgroundStyles for full background setup:
 * const HeroSection = styled.div`
 *   ${props => getOptimizedBackgroundStyles(props.imageUrl, { width: 1920, height: 1080 })}
 * `;
 */

/**
 * Optimize a URL for use in background-image
 * @param {string} imageUrl - Original image URL
 * @param {object} options - Optimization options
 * @param {number} options.width - Target width (default: 1920)
 * @param {number} options.height - Target height (default: 1080)
 * @param {number} options.quality - Quality 1-100 (default: 85)
 * @returns {string} Optimized image URL
 */
export const optimizeBackgroundImage = (imageUrl, options = {}) => {
  if (!imageUrl) {
    return '';
  }

  const { width = 1920, height = 1080, quality = 85 } = options;

  // Don't optimize local images
  if (!imageUrl.startsWith('http')) {
    return imageUrl;
  }

  // Detect image source
  const isUnsplash = imageUrl.includes('unsplash.com');
  const isWikimedia = imageUrl.includes('wikimedia.org') || imageUrl.includes('wikipedia.org');

  if (isUnsplash) {
    return optimizeUnsplash(imageUrl, { width, height, quality });
  } else if (isWikimedia) {
    return optimizeWikimedia(imageUrl, { width, height, quality });
  } else {
    return optimizeImage(imageUrl, { width, height, quality, format: 'webp' });
  }
};

/**
 * Get complete optimized background styles for styled-components
 * @param {string} imageUrl - Original image URL
 * @param {object} options - Options
 * @param {number} options.width - Target width (default: 1920)
 * @param {number} options.height - Target height (default: 1080)
 * @param {number} options.quality - Quality (default: 85)
 * @param {string} options.position - Background position (default: 'center')
 * @param {string} options.size - Background size (default: 'cover')
 * @returns {string} CSS string for background styles
 */
export const getOptimizedBackgroundStyles = (imageUrl, options = {}) => {
  const {
    width = 1920,
    height = 1080,
    quality = 85,
    position = 'center',
    size = 'cover'
  } = options;

  const optimizedUrl = optimizeBackgroundImage(imageUrl, { width, height, quality });

  return `
    background-image: url(${optimizedUrl});
    background-size: ${size};
    background-position: ${position};
    background-repeat: no-repeat;
  `;
};

export default optimizeBackgroundImage;
